import type {
  Configuration,
  CreateIngestionPipelineInput,
  CreateJobTemplateInput,
  CreateSparkEngineInput,
  CreateSqlEngineInput,
  StartSparkJobInput,
  StartSqlQueryExecutionInput,
  UpdateJobTemplateInput,
} from '@repo/api-gw-sdk';
import { LakehouseApi } from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/lakehouse';

/**
 * Wraps an async function to handle 204 No Content responses gracefully.
 * The generated SDK tries to parse empty 204 responses as JSON, throwing a SyntaxError.
 * This wrapper catches that specific error and treats it as success.
 */
const handle204 = async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
  try {
    return await fn();
  } catch (error) {
    // 204 No Content causes JSON parsing to fail - treat as success
    if (
      error instanceof SyntaxError ||
      (error instanceof Error &&
        error.message.includes('Unexpected end of JSON input'))
    ) {
      return undefined;
    }
    throw error;
  }
};

export const lakehouse = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const lakehouseApi = new LakehouseApi(config);

  return {
    // SQL Engine operations
    useListSqlEngines: () =>
      httpClient.execute(
        lakehouseApi.listSqlEngines.bind(lakehouseApi),
        baseUrl,
        'listSqlEngines',
        currentProjectId
      ),
    createSqlEngine: (request: CreateSqlEngineInput) =>
      lakehouseApi.createSqlEngine(currentProjectId, request),
    getSqlEngine: (sqlEngineId: string) =>
      lakehouseApi.getSqlEngine(currentProjectId, sqlEngineId),
    deleteSqlEngine: (sqlEngineId: string) =>
      handle204(() =>
        lakehouseApi.deleteSqlEngine(currentProjectId, sqlEngineId)
      ),
    startSqlEngine: (sqlEngineId: string) =>
      lakehouseApi.startSqlEngine(currentProjectId, sqlEngineId),
    stopSqlEngine: (sqlEngineId: string) =>
      lakehouseApi.stopSqlEngine(currentProjectId, sqlEngineId),

    // SQL Query Execution operations
    useListSqlQueryExecutions: () =>
      httpClient.execute(
        lakehouseApi.listSqlQueryExecutions.bind(lakehouseApi),
        baseUrl,
        'listSqlQueryExecutions',
        currentProjectId
      ),
    startSqlQueryExecution: (request: StartSqlQueryExecutionInput) =>
      lakehouseApi.startSqlQueryExecution(currentProjectId, request),
    getSqlQueryExecution: (sqlQueryExecutionId: string) =>
      lakehouseApi.getSqlQueryExecution(currentProjectId, sqlQueryExecutionId),
    getSqlQueryExecutionResult: (
      sqlQueryExecutionId: string,
      pageToken?: string,
      pageSize?: number
    ) =>
      lakehouseApi.getSqlQueryExecutionResult(
        currentProjectId,
        sqlQueryExecutionId,
        pageToken,
        pageSize
      ),

    // Spark Engine operations
    useListSparkEngines: () =>
      httpClient.execute(
        lakehouseApi.listSparkEngines.bind(lakehouseApi),
        baseUrl,
        'listSparkEngines',
        currentProjectId
      ),
    createSparkEngine: (request: CreateSparkEngineInput) =>
      lakehouseApi.createSparkEngine(currentProjectId, request),
    getSparkEngine: (sparkEngineId: string) =>
      lakehouseApi.getSparkEngine(currentProjectId, sparkEngineId),
    deleteSparkEngine: (sparkEngineId: string) =>
      handle204(() =>
        lakehouseApi.deleteSparkEngine(currentProjectId, sparkEngineId)
      ),
    startSparkEngine: (sparkEngineId: string) =>
      lakehouseApi.startSparkEngine(currentProjectId, sparkEngineId),
    stopSparkEngine: (sparkEngineId: string) =>
      lakehouseApi.stopSparkEngine(currentProjectId, sparkEngineId),

    // Spark Job operations
    useListSparkJobs: () =>
      httpClient.execute(
        lakehouseApi.listSparkJobs.bind(lakehouseApi),
        baseUrl,
        'listSparkJobs',
        currentProjectId
      ),
    startSparkJob: (request: StartSparkJobInput) =>
      lakehouseApi.startSparkJob(currentProjectId, request),

    // Job Template operations
    useListJobTemplates: () =>
      httpClient.execute(
        lakehouseApi.listJobTemplates.bind(lakehouseApi),
        baseUrl,
        'listJobTemplates',
        currentProjectId
      ),
    createJobTemplate: (request: CreateJobTemplateInput) =>
      lakehouseApi.createJobTemplate(currentProjectId, request),
    updateJobTemplate: (
      jobTemplateId: string,
      request: UpdateJobTemplateInput
    ) =>
      lakehouseApi.updateJobTemplate(currentProjectId, jobTemplateId, request),
    deleteJobTemplate: (jobTemplateId: string) =>
      handle204(() =>
        lakehouseApi.deleteJobTemplate(currentProjectId, jobTemplateId)
      ),

    // Ingestion Pipeline operations
    useListIngestionPipelines: () =>
      httpClient.execute(
        lakehouseApi.listIngestionPipelines.bind(lakehouseApi),
        baseUrl,
        'listIngestionPipelines',
        currentProjectId
      ),
    createIngestionPipeline: (request: CreateIngestionPipelineInput) =>
      lakehouseApi.createIngestionPipeline(currentProjectId, request),
    getIngestionPipeline: (ingestionPipelineId: string) =>
      lakehouseApi.getIngestionPipeline(currentProjectId, ingestionPipelineId),
    deleteIngestionPipeline: (ingestionPipelineId: string) =>
      handle204(() =>
        lakehouseApi.deleteIngestionPipeline(
          currentProjectId,
          ingestionPipelineId
        )
      ),
    enableIngestionPipeline: (ingestionPipelineId: string) =>
      lakehouseApi.enableIngestionPipeline(
        currentProjectId,
        ingestionPipelineId
      ),
    disableIngestionPipeline: (ingestionPipelineId: string) =>
      lakehouseApi.disableIngestionPipeline(
        currentProjectId,
        ingestionPipelineId
      ),
  };
};
